import { useEffect, useSyncExternalStore } from "react";
import { getJson, onApiCacheClear } from "@/lib/api";

/**
 * สิทธิ์ บัญชีโฆษณา 2 ของ user ปัจจุบัน — คำตอบเดียวกับ backend (GET /api/accounting/me)
 *   - allowed: true = เข้าได้ (สมาชิกทีม/ผู้ดูแล/admin) · false = ไม่มีสิทธิ์ (backend ตอบ 403 เท่านั้น) · null = กำลังเช็ค/เช็คไม่สำเร็จ
 *   - error: เช็คไม่สำเร็จ (5xx / เน็ตหลุด) — ไม่ใช่ "ไม่มีสิทธิ์" · ห้ามซ่อนเมนู/เด้งออกเงียบ ๆ ให้โชว์แถบแจ้ง + ปุ่มลองใหม่
 *   - canSwitchTeam: สลับดู/จัดการทีมอื่นได้ไหม (ผู้ดูแลใน settings `adlab_supervisor_usernames` / admin ทุกคน — มีทีมหรือไม่ก็ได้)
 *   - ownTeamId: id ทีมของตัวเองใน kk ("" = ไม่มีทีม) — ใช้เป็นทีมเริ่มต้น + ป้าย "ทีมฉัน" ในตัวสลับ (ไม่ได้จำกัดสิทธิ์สลับ)
 *   - teamLead: เป็นหัวหน้าทีมของทีมตัวเอง (admin ตั้งที่หน้าจัดการผู้ใช้งาน /admin/users) — ใช้กับ canManageTeamMoney เท่านั้น
 * ใช้ซ่อนเมนู + gate route ไม่ให้คนนอกทีมเห็น/เข้า (ไม่ให้ขึ้น "ยังไม่ได้รับอนุญาต")
 *
 * จำผลข้าม remount (ต่อ session) เฉพาะคำตอบที่ชัด (สำเร็จ / 403) · error ไม่จำ — เรียกซ้ำได้ด้วย retry()
 * ทุกที่ที่ใช้ hook นี้ (เมนู + หน้า) เห็นสถานะเดียวกัน
 */
export interface AccountingAccess {
  allowed: boolean | null;
  canSwitchTeam: boolean;
  ownTeamId: string;
  teamLead: boolean;
  error: string | null;
  retry: () => void;
}

type Snapshot = Omit<AccountingAccess, "retry">;

const PENDING: Snapshot = { allowed: null, canSwitchTeam: false, ownTeamId: "", teamLead: false, error: null };
let snap: Snapshot = PENDING;
let inflight = false;
let generation = 0; // เพิ่มทุกครั้งที่ล้าง cache — คำตอบของรอบเก่า (ก่อน logout) ห้ามเขียนทับ
const listeners = new Set<() => void>();

function publish(next: Snapshot): void {
  snap = next;
  listeners.forEach((l) => l());
}

/** เช็คสิทธิ์ถ้ายังไม่มีคำตอบที่ชัด (ไม่ยิงซ้ำระหว่างรอ) */
export function loadAccountingAccess(): void {
  if (inflight || snap.allowed !== null) return;
  inflight = true;
  const gen = generation;
  if (snap.error) publish(PENDING); // กำลังลองใหม่ — เอาแถบ error ออกระหว่างรอ
  void getJson<{ canSwitchTeam?: boolean; ownTeamId?: string; teamLead?: boolean }>("/api/accounting/me").then((r) => {
    if (gen !== generation) return; // cache ถูกล้างระหว่างรอ — รอบใหม่จะเช็คเอง
    inflight = false;
    if (r.success) {
      publish({ allowed: true, canSwitchTeam: !!r.data?.canSwitchTeam, ownTeamId: String(r.data?.ownTeamId ?? ""), teamLead: !!r.data?.teamLead, error: null });
    } else if (r.status === 403) {
      publish({ allowed: false, canSwitchTeam: false, ownTeamId: "", teamLead: false, error: null });
    } else {
      publish({ ...PENDING, error: r.error || "เช็คสิทธิ์ไม่สำเร็จ" });
    }
  });
}

/** ทิ้งคำตอบเดิมแล้วเช็คใหม่ — logout (onApiCacheClear) หรือหลัง admin แก้รายชื่อผู้ดูแล (settings แท็บบัญชีโฆษณา) */
export function resetAccountingAccess(): void {
  generation++;
  inflight = false;
  publish({ ...PENDING }); // object ใหม่ → hook ที่ mount อยู่ render ใหม่แล้วเช็คใหม่ (แม้เดิมยังรอคำตอบอยู่)
}
onApiCacheClear(resetAccountingAccess); // logout/เคลียร์ cache → เช็คใหม่

/** สถานะล่าสุด (ใช้กับ useSyncExternalStore + test) */
export function getAccountingAccessSnapshot(): Snapshot {
  return snap;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useAccountingAccess(): AccountingAccess {
  const s = useSyncExternalStore(subscribe, getAccountingAccessSnapshot);
  // เช็คอัตโนมัติเฉพาะตอนยังไม่มีคำตอบ + ไม่ได้ error (error = รอผู้ใช้กดลองใหม่ ไม่ยิงวนเอง)
  useEffect(() => {
    if (s.allowed === null && !s.error) loadAccountingAccess();
  }, [s]);
  return { ...s, retry: loadAccountingAccess };
}
